import { Box, Divider, Typography } from "@mui/material";
import { groupBy } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SolidGradientColorPicker from "../../../../../../../components/common/SolidGradientColorPicker/SolidGradientColorPicker";
import { useAppStore } from "../../../../../../../hooks/useStores";
import useDesignEditorUtils, { FeatureType, Property } from "../../../../../../CustomUtils/UseDesignEditor";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import { setFabricElementGradient } from "../../../../../../core/utils/fabric";
import {
	fabricGradientToString,
	isGradient,
	parseGradientString,
	updateGradientStopColor,
} from "../../../../../../core/utils/gradient_color_utils.ts";
import { useActiveObject } from "../../../../../../hooks/useActiveObject";
import UseBrandConfigs from "../../../../../../hooks/useBrandConfigs.tsx";
import { useEditor } from "../../../../../../hooks/useEditor";
import { LayerType } from "../../../../../../types";
import SelectedBrandColors from "../Common/SelectedBrandColors/SelectedBrandColors.tsx";
import styles from "./ShapeFillColor.module.scss";

const ShapeFillColor = () => {
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs" });
	const { campaignStore, brandsDefinitionStore } = useAppStore();
	const activeObject = useActiveObject() as any;
	const [color, setColor] = useState("");
	const [selectedStopIndex, setSelectedStopIndex] = useState(0);
	const { EditAllAds, getRgbAndOpacityFromRgba, updateColorOpacity } = useDesignEditorUtils();
	const editor = useEditor();
	const { modifyActiveObjectProperty } = UseBrandConfigs();
	const isFabricShape =
		activeObject?.type === LayerType.STATIC_RECT ||
		activeObject?.type === LayerType.STATIC_CIRCLE ||
		activeObject?.type === LayerType.STATIC_TRIANGLE;

	useEffect(() => {
		if (activeObject && activeObject.type === LayerType.STATIC_VECTOR) {
			const objects = activeObject._objects[0]._objects;
			const objectColors = groupBy(objects, "fill");
			campaignStore.vectorColors = {
				...campaignStore.vectorColors,
				colors: Object.keys(objectColors),
				colorMap: activeObject.colorMap,
			};

			Object.keys(campaignStore.vectorColors.colorMap).map((color, _index) => {
				campaignStore.prevColor = color;
			});

			const currentColorValue = campaignStore?.vectorColors?.colorMap[campaignStore?.prevColor];

			// Check if the current color is a gradient
			if (typeof currentColorValue === "string" && isGradient(currentColorValue)) {
				setColor(currentColorValue);
			} else {
				const currentColor = updateColorOpacity(currentColorValue, activeObject.opacity);
				setColor(currentColor);
			}
		} else if (isFabricShape) {
			const currentFill = activeObject.fill;

			if (currentFill && typeof currentFill === "object" && currentFill.colorStops) {
				const gradientString = fabricGradientToString(currentFill);
				setColor(gradientString || "#000000");
			} else if (typeof currentFill === "string" && isGradient(currentFill)) {
				// It's already a gradient string
				setColor(currentFill);
			} else {
				// It's a solid color
				setColor(updateColorOpacity(currentFill, activeObject.opacity));
			}
		}
	}, [activeObject]);

	const handleStopSelection = (index: number) => {
		setSelectedStopIndex(index);
	};

	const getFinalColor = (newColor: string, isBrandColor: boolean): string => {
		const currentFillIsGradient = isGradient(color);
		const newColorIsGradient = isGradient(newColor);

		// If brand color is selected and current fill is a gradient, update the selected stop
		if (isBrandColor && currentFillIsGradient && !newColorIsGradient) {
			const updatedGradient = updateGradientStopColor(color, newColor, selectedStopIndex);
			return updatedGradient || newColor;
		}

		return newColor;
	};

	const applyVectorColor = (prev: string, finalColor: string, objectRef: any) => {
		const gradientData = parseGradientString(finalColor);
		if (gradientData) {
			const sameObjects = objectRef.objectColors[prev];
			if (sameObjects) {
				sameObjects.forEach((obj: any) => {
					setFabricElementGradient(obj, gradientData.angle, gradientData.colorStops);
				});
			}
			campaignStore.vectorColors = {
				...campaignStore.vectorColors,
				colorMap: {
					...campaignStore.vectorColors.colorMap,
					[prev]: finalColor,
				},
			};
			objectRef.colorMap[prev] = finalColor;
			editor?.canvas.requestRenderAll();
			editor?.canvas.canvas.fire("object:modified", { target: objectRef });
			if (!campaignStore.isEditingAllAds) editor?.history.save();
			EditAllAds(LayerType.STATIC_VECTOR, activeObject?.shapeType, FeatureType.COLOR, finalColor);
		}
	};

	const applyVectorSolidColor = (
		prev: string,
		finalColor: string,
		objectRef: any,
		result: any,
		brandColor: boolean,
	) => {
		objectRef?.updateLayerColor(prev, result?.rgb ?? finalColor);
		campaignStore.vectorColors = {
			...campaignStore.vectorColors,
			colorMap: {
				...campaignStore.vectorColors.colorMap,
				[prev]: result?.rgb ?? finalColor,
			},
		};
		editor?.objects.update(
			{
				opacity: brandColor ? undefined : (result?.opacity ?? 1),
				brandId: undefined,
			},
			undefined,
			!campaignStore.isEditingAllAds,
		);
		EditAllAds(LayerType.STATIC_VECTOR, activeObject?.shapeType, FeatureType.COLOR, result?.rgb ?? finalColor);
	};

	const applyFabricShapeColor = (finalColor: string, objectRef: any) => {
		const gradientData = parseGradientString(finalColor);
		if (gradientData && objectRef) {
			setFabricElementGradient(objectRef, gradientData.angle, gradientData.colorStops);
			editor?.canvas.canvas.requestRenderAll();
			if (!campaignStore.isEditingAllAds) editor?.history.save();
			EditAllAds(activeObject.type, activeObject?.shapeType, FeatureType.COLOR, finalColor);
		}
	};

	const applyFabricShapeSolidColor = (finalColor: string, result: any, brandColor: boolean) => {
		editor?.objects.update(
			{
				fill: result?.rgb ?? finalColor,
				opacity: brandColor ? undefined : (result?.opacity ?? 1),
				brandId: undefined,
			},
			undefined,
			!campaignStore.isEditingAllAds,
		);
		EditAllAds(activeObject.type, activeObject?.shapeType, FeatureType.COLOR, result?.rgb ?? finalColor);
	};

	const renderGradientStopSelector = () => {
		if (!isGradient(color)) return null;

		const gradientData = parseGradientString(color);
		if (!gradientData || gradientData.colorStops.length <= 1) return null;

		return (
			<Box className={styles.gradientStopSelector}>
				<Typography className={styles.label}>{t("selectGradientStop")}</Typography>
				<Box className={styles.stopsContainer}>
					{gradientData.colorStops.map((stop, index) => (
						<Box
							key={index}
							onClick={() => handleStopSelection(index)}
							className={`${styles.stopBox} ${selectedStopIndex === index ? styles.selected : ""}`}
							style={{ backgroundColor: stop.color }}
							title={`${t("stop")} ${index + 1} (${Math.round(stop.offset * 100)}%)`}
						/>
					))}
				</Box>
			</Box>
		);
	};

	const changeBackgroundColor = (prev: string, next: string, brandColor: boolean = false) => {
		const objectRef = activeObject;
		const result = getRgbAndOpacityFromRgba(next);

		// Determine final color (brand color may update gradient stop)
		const finalColor = getFinalColor(next, brandColor);
		setColor(finalColor);

		const isFinalGradient = isGradient(finalColor);

		// Apply color based on object type
		if (objectRef && objectRef.type === LayerType.STATIC_VECTOR) {
			if (isFinalGradient) {
				applyVectorColor(prev, finalColor, objectRef);
			} else {
				applyVectorSolidColor(prev, finalColor, objectRef, result, brandColor);
			}
		} else if (isFabricShape) {
			if (isFinalGradient) {
				applyFabricShapeColor(finalColor, objectRef);
			} else {
				applyFabricShapeSolidColor(finalColor, result, brandColor);
			}
		}
	};

	return (
		<>
			<Box className={styles.container}>
				<MainTabHeader title={t("fillColor")} />
				<Box className={`${styles.tabs} ${styles.fullHeight}`}>
					<Box className={styles.ColorFillContent}>
						<Box className={styles.colorPickerContainer}>
							<SolidGradientColorPicker
								color={color}
								onChange={(newColor: string) => {
									setColor(newColor);
									changeBackgroundColor(campaignStore.prevColor, newColor);
									modifyActiveObjectProperty(activeObject, Property.COLOR_NUMBER, undefined);
								}}
								hideColorTypeBtns={false}
							/>
						</Box>

						<Divider className={styles.mainDivider} />
						{brandsDefinitionStore.globalSelectedBrand?.color_pallete?.colors?.some(Boolean) &&
							renderGradientStopSelector()}
						<SelectedBrandColors
							activeObject={activeObject}
							updateColor={(brandColor: string) => {
								changeBackgroundColor(campaignStore.prevColor, brandColor, true);
							}}
							property={Property.COLOR_NUMBER}
						/>
					</Box>
				</Box>
			</Box>
		</>
	);
};

const ObservedComponent = observer(ShapeFillColor);
export default ObservedComponent;
